<script setup>
import { defineProps, ref, computed, onMounted, onBeforeUnmount, inject } from "vue";
import { Link, usePage } from "@inertiajs/vue3";
import ApplicationLogo from "@/Components/ApplicationLogo.vue";
import NavLink from "@/Components/NavLink.vue";
import MobileMenu from "@/Components/MobileMenu.vue";
import DropdownLink from "@/Components/DropdownLink.vue";
const route = inject('route');
const page = usePage();

const currentPath = computed(() => page.url.split('?')[0]);

const isMenuActive = (routeName) => {
  if (!routeName) return false;
  try {
    const path = new URL(route(routeName)).pathname;
    if (path === '/') return currentPath.value === '/';
    return currentPath.value === path || currentPath.value.startsWith(path + '/');
  } catch {
    return false;
  }
};

const isGroupActive = (submenu) =>
  Array.isArray(submenu) && submenu.some((s) => isMenuActive(s.linkurl));

const props = defineProps({
  menu: {
    type: Array,
    required: true,
  },
});

// State untuk menyimpan ID dropdown yang terbuka
const openDropdownId = ref(null);
const showDropdown = ref(null);
const showMobileMenu = ref(false);

// Fungsi untuk membuka dropdown
const openDropdown = (id) => {
  openDropdownId.value = id;
};

// Fungsi untuk menutup dropdown
const closeDropdown = () => {
  openDropdownId.value = null;
};
const isMobileMenuVisible = ref(false);
const activeMobileDropdownId = ref(null);

const toggleMobileMenu = () => {
  isMobileMenuVisible.value = !isMobileMenuVisible.value;
  activeMobileDropdownId.value = null; // Reset dropdown saat menu mobile dibuka/tutup
};

const toggleMobileDropdown = (id) => {
  activeMobileDropdownId.value =
    activeMobileDropdownId.value === id ? null : id;
};

const closeMobileMenu = () => {
  isMobileMenuVisible.value = false;
  activeMobileDropdownId.value = null;
};

// Fungsi untuk menutup menu saat klik di luar
const handleClickOutside = (event) => {
  if (
    !event.target.closest(".mobile-menu") &&
    !event.target.closest(".toggle-button")
  ) {
    closeMobileMenu();
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>
<style scoped>
/* Basic header styles */
</style>

<template>
  <div>
    <header class="fixed inset-x-0 top-0 z-50 text-gray-800 bg-white shadow-md">
      <div class="flex items-center h-16 max-w-screen-xl gap-8 px-4 mx-auto sm:px-6 lg:px-8">
        <div class="flex items-center gap-3">
          <ApplicationLogo class="w-8 h-8" :logo="$page.props.apps.logo" />
          <div class="flex flex-col">
            <h1 class="hidden text-base font-bold text-maroon-800 sm:block font-roboto-slab">
              {{ $page.props.apps.namaapps }}
            </h1>
            <h1 class="text-xs font-bold text-maroon-800 sm:hidden font-roboto-slab">
              {{ $page.props.apps.namaapps }}
            </h1>
          </div>
        </div>

        <div v-if="$page.props.auth.user">
          <NavLink routeName="dashboard" componentName="Dashboard" class="hidden text-gray-700 md:block">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6">
              <path
                d="M11.47 3.841a.75.75 0 0 1 1.06 0l8.69 8.69a.75.75 0 1 0 1.06-1.061l-8.689-8.69a2.25 2.25 0 0 0-3.182 0l-8.69 8.69a.75.75 0 1 0 1.061 1.06l8.69-8.689Z" />
              <path
                d="m12 5.432 8.159 8.159c.03.03.06.058.091.086v6.198c0 1.035-.84 1.875-1.875 1.875H15a.75.75 0 0 1-.75-.75v-4.5a.75.75 0 0 0-.75-.75h-3a.75.75 0 0 0-.75.75V21a.75.75 0 0 1-.75.75H5.625a1.875 1.875 0 0 1-1.875-1.875v-6.198a2.29 2.29 0 0 0 .091-.086L12 5.432Z" />
            </svg>
          </NavLink>
        </div>

        <div class="flex items-center justify-end flex-1 md:justify-between">
          <nav aria-label="Global" class="hidden md:block">
            <ul class="flex items-center gap-6 text-sm">
              <li v-for="main_menu in menu" :key="main_menu.id">
                <div class="hidden md:relative md:block">
                  <button @mouseenter="openDropdown(main_menu.id)"
                    class="flex items-center text-sm font-medium transition rounded-lg px-2 py-1 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-100 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
                    :class="{
                      'justify-between': main_menu.submenu && main_menu.submenu.length > 0,
                      'justify-start': !(main_menu.submenu && main_menu.submenu.length > 0),
                      'bg-indigo-50 text-indigo-600': isMenuActive(main_menu.linkurl) || isGroupActive(main_menu.submenu),
                      'text-gray-800': !isMenuActive(main_menu.linkurl) && !isGroupActive(main_menu.submenu),
                    }">
                    <div class="flex items-center">
                      <template v-if="main_menu.ismain === 0">
                        <a v-if="main_menu.linkurl" :href="route(main_menu.linkurl)" @click="closeMobileMenu"
                          class="transition"
                          :class="isMenuActive(main_menu.linkurl) ? 'text-indigo-600 font-semibold' : 'text-gray-700 hover:text-gray-900'">
                          <i v-if="main_menu.icon_main" :class="main_menu.icon_main" class="mr-2"></i>
                          {{ main_menu.namamenu }}
                        </a>
                        <span v-else class="text-gray-700">
                          <i v-if="main_menu.icon_main" :class="main_menu.icon_main" class="mr-2"></i>
                          {{ main_menu.namamenu }}
                        </span>
                      </template>
                      <template v-else>
                        <span :class="isGroupActive(main_menu.submenu) ? 'text-indigo-600 font-semibold' : 'text-gray-700'">
                          {{ main_menu.namamenu }}
                        </span>
                      </template>
                    </div>
                    <svg v-if="main_menu.submenu && main_menu.submenu.length > 0"
                      class="w-3 h-3 ml-2 transition-transform cursor-pointer fill-current"
                      :class="{ 'rotate-180': openDropdownId === main_menu.id }" xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 512 512">
                      <path
                        d="M233.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 338.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z" />
                    </svg>
                  </button>

                  <div v-if="
                    main_menu.submenu &&
                    main_menu.submenu.length > 0 &&
                    openDropdownId === main_menu.id
                  " @mouseenter="openDropdown(main_menu.id)" @mouseleave="closeDropdown"
                    class="absolute z-10 w-56 mt-2 -translate-x-1/2 bg-white border border-gray-100 divide-y divide-gray-100 rounded-md shadow-lg left-1/2 top-9"
                    role="menu">
                    <div v-for="(submenu, index) in main_menu.submenu" :key="index" class="p-2">
                      <a :href="route(submenu.linkurl)" @click="closeMobileMenu"
                        class="block px-4 py-2 text-sm rounded-lg hover:bg-gray-100"
                        :class="isMenuActive(submenu.linkurl)
                          ? 'bg-indigo-50 text-indigo-600 font-semibold'
                          : 'text-gray-700 hover:text-gray-900'"
                        role="menuitem">
                        {{ submenu.namamenu }}
                      </a>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </nav>

          <div class="flex items-center gap-4">
            <button @click="toggleMobileMenu"
              class="toggle-button block rounded-sm bg-sky-800 p-2.5 text-white transition hover:bg-sky-700 md:hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400">
              <span class="sr-only">Toggle menu</span>
              <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24"
                stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <div v-show="isMobileMenuVisible"
              class="absolute right-0 z-20 w-full mt-2 text-gray-800 bg-white border border-gray-100 divide-y divide-gray-100 rounded-md shadow-lg mobile-menu top-12">
              <Link v-if="$page.props.auth.user" :href="route('dashboard')" @click="closeMobileMenu"
                class="block px-4 py-2 text-sm hover:bg-gray-100 rounded"
                :class="isMenuActive('dashboard') ? 'bg-indigo-50 text-indigo-600 font-semibold' : 'text-gray-700'">
                <i class="fa-solid fa-house-user"></i> Beranda
              </Link>
              <a
                v-if="$page.props.auth.user"
                :href="route('home')"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center gap-2 px-4 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded"
                @click="closeMobileMenu"
              >
                <i class="fa-solid fa-arrow-up-right-from-square text-xs"></i>
                Lihat Website
              </a>
              <ul class="flex flex-col gap-2 p-2">
                <li v-for="mainMenu in menu" :key="mainMenu.id">
                  <div>
                    <button @click="toggleMobileDropdown(mainMenu.id)"
                      class="flex items-center justify-between w-full px-4 py-2 text-sm font-medium transition rounded-lg hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
                      :class="(isMenuActive(mainMenu.linkurl) || isGroupActive(mainMenu.submenu))
                        ? 'bg-indigo-50 text-indigo-600'
                        : 'text-gray-700'">
                      <span>
                        <i v-if="mainMenu.icon_main" :class="mainMenu.icon_main" class="w-5 h-5 mr-2"></i>
                        {{ mainMenu.namamenu }}
                      </span>
                      <svg v-if="mainMenu.submenu && mainMenu.submenu.length > 0"
                        class="w-3 h-3 transition-transform cursor-pointer fill-current" :class="{
                          'rotate-180': activeMobileDropdownId === mainMenu.id,
                        }" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                        <path
                          d="M233.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 338.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z" />
                      </svg>
                    </button>
                    <div v-if="
                      mainMenu.submenu &&
                      mainMenu.submenu.length > 0 &&
                      activeMobileDropdownId === mainMenu.id
                    " class="pl-4 mt-1">
                      <ul class="flex flex-col gap-1">
                        <li v-for="(submenu, index) in mainMenu.submenu" :key="index">
                          <a :href="route(submenu.linkurl)" @click="closeMobileMenu"
                            class="block px-4 py-2 text-sm rounded-lg hover:bg-gray-100"
                            :class="isMenuActive(submenu.linkurl)
                              ? 'bg-indigo-50 text-indigo-600 font-semibold'
                              : 'text-gray-700 hover:text-gray-900'">
                            {{ submenu.namamenu }}
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <!-- Website link (desktop) -->
          <a
            v-if="$page.props.auth.user"
            :href="route('home')"
            target="_blank"
            rel="noopener noreferrer"
            class="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors whitespace-nowrap"
            title="Buka halaman website publik di tab baru"
          >
            <i class="fa-solid fa-arrow-up-right-from-square text-[11px]"></i>
            Lihat Website
          </a>

          <div v-if="$page.props.auth.user" class="relative sm:flex sm:gap-4">
            <button @click="showDropdown = !showDropdown"
              class="px-5 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-rose-900 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400">
              <i class="fa-regular fa-user"></i>

              <span class="hidden sm:inline">
                {{
                  $page.props.auth.user
                    ? $page.props.auth.user.name
                    : $page.props.siat.nim
                }}
              </span>
            </button>
            <div v-if="showDropdown" @click="showDropdown = false"
              class="absolute right-0 z-20 w-48 mt-2 bg-white border border-gray-100 divide-y divide-gray-100 rounded-md shadow-lg top-12">
              <Link v-if="$page.props.auth.user" :href="route('profile.edit')" @click="closeMobileMenu"
                class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Profil</Link>
              <DropdownLink v-if="$page.props.auth.user" :href="route('logout')" method="post" as="button">Keluar
              </DropdownLink>
            </div>
          </div>
        </div>
      </div>
    </header>
  </div>
</template>
